import { Box, Collapse, Flex, useDisclosure } from '@chakra-ui/react';
import { datadogLogs } from '@datadog/browser-logs';
import { Cog6ToothIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button, IconButton, Tooltip } from '@main/complykit';
import { Card } from '@main/complykit/components/card';
import { Content } from '@main/complykit/components/slots';
import { Integration_Names_Enum } from '@main/graphql/types.generated';
import { toError } from '@main/shared/utils';
import { errorToast, successToast, useAlertDialog } from '@main/ui';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IntegrationDetails } from '../integration-details';
import { AnyConfigRef, ConfigComponentType } from '../integration-handler-config/types';
import { IntegrationLogo } from '../integration-logo';

export type ConfigurableIntegrationCardProps = {
  orgIntegrationId: string;
  providerName: Integration_Names_Enum;
  connectorName?: string;
  isDisabled: boolean;
  isConfigurable: boolean;
  evidenceConfig?: unknown;
  onConfigChange: (payload: Record<string, unknown>) => void;
  onDelete: () => void;
  ConfigComponent: ConfigComponentType;
};

export const ConfigurableIntegrationCard = ({
  orgIntegrationId,
  providerName,
  connectorName,
  isDisabled,
  isConfigurable,
  evidenceConfig,
  onConfigChange,
  onDelete,
  ConfigComponent,
}: ConfigurableIntegrationCardProps) => {
  const { t } = useTranslation('integrations');

  const { openDialog } = useAlertDialog();
  const configCollapse = useDisclosure();
  const helpMessage = isDisabled ? t('disabledMessage') : t('enabledMessage');
  const configRef = useRef<AnyConfigRef>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSaveClick = async () => {
    setIsSaving(true);
    try {
      const payload = await configRef.current?.submit();
      if (!payload) return;
      onConfigChange(payload);
      successToast(t('configureForm.successMsg'));
    } catch (error) {
      errorToast(
        t('errorMessages.updateFailed', {
          integrationName: t(`integrations.${providerName}.name`),
        }),
      );
      datadogLogs.logger.error(
        `Failed updating ${providerName} integration`,
        { orgIntegrationId },
        toError(error),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = () =>
    openDialog({
      dialogHeader: t('deleteConfigDialog.header'),
      dialogContent: t('deleteConfigDialog.content', {
        integrationName: t(`integrations.${providerName}.name`),
      }),
      confirmAction: {
        children: t('deleteConfigDialog.confirmBtn'),
        onClick: async () => {
          setIsDeleting(true);
          try {
            await configRef.current?.delete?.();
            configCollapse.onClose();
            onDelete();
          } finally {
            setIsDeleting(false);
          }
        },
      },
    });

  return (
    <Card>
      <Content>
        <Flex align="start" gap={4}>
          <IntegrationLogo name={providerName} />
          <Flex grow={1}>
            <IntegrationDetails
              providerName={providerName}
              connectorName={connectorName}
              helpMessage={helpMessage}
            />
          </Flex>

          {evidenceConfig ? (
            <Flex gap={2}>
              <IconButton
                label={t('configureForm.configureBtn')}
                icon={Cog6ToothIcon}
                variant="soft"
                onPress={configCollapse.onToggle}
              />
              <IconButton
                variant="outline"
                tone="critical"
                label={t('configureForm.deleteBtn')}
                isPending={isDeleting}
                icon={TrashIcon}
                onPress={handleDeleteClick}
              />
            </Flex>
          ) : (
            <Tooltip
              content={t('configureForm.nonConfigurableTooltip')}
              isDisabled={isConfigurable}
            >
              <Button
                isDisabled={!isConfigurable || configCollapse.isOpen}
                onPress={configCollapse.onOpen}
              >
                {t('configureForm.configureBtn')}
              </Button>
            </Tooltip>
          )}
        </Flex>

        <Collapse in={configCollapse.isOpen}>
          <Box mt={8} mb={4} mx={0.5}>
            <ConfigComponent
              ref={configRef}
              orgIntegrationId={orgIntegrationId}
              providerName={providerName}
              evidenceConfig={evidenceConfig}
              isSubmitting={isSaving}
            />
          </Box>

          <Flex gap={2} w="full" justify="end">
            <Button variant="outline" onPress={configCollapse.onClose}>
              {t('configureForm.cancelBtn')}
            </Button>
            <Button isPending={isSaving} onPress={() => void handleSaveClick()}>
              {t('configureForm.saveBtn')}
            </Button>
          </Flex>
        </Collapse>
      </Content>
    </Card>
  );
};
